/* Modal shell for the homepage product finder.
 *
 * The assistant itself is llm-collection-helper.js, untouched — this only opens and closes
 * the box around it. Kept separate so the helper stays the single implementation of the
 * conversation, and so this file can be deleted without touching it.
 *
 * Accessibility is the whole job here. A modal on a site with an older customer base has to
 * behave predictably: Escape closes it, Tab cannot wander behind it, focus lands somewhere
 * useful on open and returns to the button on close, and the page behind does not scroll
 * away underneath.
 */
(function () {
  var openBtn = document.querySelector('.llm-finder__open');
  var overlay = document.getElementById('llm-finder-overlay');
  if (!openBtn || !overlay) return;

  var dialog = overlay.querySelector('.llm-finder__dialog');
  var closeBtn = overlay.querySelector('.llm-finder__close');
  var lastFocused = null;

  /* PORTAL the overlay to <body>. It is authored inside the hero, and `.llm-hs__inner` sets
     `position: relative; z-index: 2` — which creates a stacking context, so the overlay's own
     z-index only ever competed INSIDE it. The whole modal therefore sat at z-index 2 against
     a header at 1000, and the header covered the top of the dialog including the close
     button. No z-index on the overlay can win that; it has to leave the context (owner
     2026-08-20). `.llm-hs` also has overflow:hidden, which would clip it regardless. */
  if (overlay.parentElement !== document.body) document.body.appendChild(overlay);

  /* "Start over" belongs in the dialog header next to the close button, where it stays put
     as the conversation grows rather than scrolling away with the content. The helper owns
     the button and toggles its `hidden` property, so MOVING the element keeps that working —
     it is the same node, just re-parented. */
  var head = overlay.querySelector('.llm-finder__head');
  function adoptResetButton() {
    var reset = dialog.querySelector('.llm-cah__reset');
    if (reset && reset.parentElement !== head) head.insertBefore(reset, closeBtn);
    return !!reset;
  }
  if (!adoptResetButton()) {
    // The helper script may not have run yet; try again shortly rather than assume order.
    var tries = 0;
    var wait = setInterval(function () {
      if (adoptResetButton() || ++tries > 20) clearInterval(wait);
    }, 100);
  }

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';

  /* Touch devices do NOT get the input focused on open. Focusing it is right on a desktop — the
     customer came here to type — but on a phone the on-screen keyboard comes up with it and covers
     the entire sheet, including the two suggestion chips and the Ask button, so the modal is
     unusable the instant it appears (owner 2026-09-04). The chips are the fastest path for someone
     who does not know what to search for, and they are exactly what gets buried.
     `pointer: coarse` is the signal, not screen width: a laptop with a touchscreen reports fine and
     keeps the autofocus, and a large tablet does not. */
  var TOUCH = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

  /* Keep the overlay inside the VISUAL viewport rather than the layout viewport. iOS does not
     shrink the layout viewport when the keyboard opens, so a `position: fixed` sheet stays pinned
     to the bottom of the PAGE — underneath the keyboard — the moment the customer taps the input
     themselves. visualViewport reports the area actually on screen; the CSS reads these two
     custom properties (see llm-hero-finder.css). Chrome on Android resizes the layout viewport and
     does not need this, but setting it there is harmless — the numbers agree. */
  var vv = window.visualViewport;
  function syncViewport() {
    if (!vv || overlay.hidden) return;
    overlay.style.setProperty('--llm-finder-top', vv.offsetTop + 'px');
    overlay.style.setProperty('--llm-finder-h', vv.height + 'px');
    /* The sheet has just got shorter, so whatever the customer is typing into may now be below the
       fold of the body scroller. `nearest` moves it the minimum distance instead of yanking it to
       the middle of a box that is only a couple of hundred pixels tall. */
    var el = document.activeElement;
    if (el && dialog.contains(el) && /^(INPUT|TEXTAREA)$/.test(el.tagName)) {
      el.scrollIntoView({ block: 'nearest' });
    }
  }
  if (vv) {
    vv.addEventListener('resize', syncViewport);
    vv.addEventListener('scroll', syncViewport);
  }

  function focusable() {
    return [].slice.call(dialog.querySelectorAll(FOCUSABLE)).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function open() {
    /* Fall back to the open button. document.activeElement is <body> when the modal is
       opened by anything other than a direct mouse click on the button (keyboard activation
       through a wrapper, a programmatic open), and returning focus to <body> on close drops
       the customer at the top of the page with no idea where they are — measured. */
    lastFocused = (document.activeElement && document.activeElement !== document.body)
      ? document.activeElement
      : openBtn;

    overlay.hidden = false;
    document.body.style.overflow = 'hidden';   // page behind must not scroll away
    openBtn.setAttribute('aria-expanded', 'true');
    syncViewport();

    /* On touch, take focus without opening the keyboard: the dialog itself, so screen readers
       announce the modal and the Tab trap has a starting point, but nothing is typed into. The
       customer taps the input when they are ready — and the visualViewport sizing above keeps the
       sheet above the keyboard when they do. */
    if (TOUCH) {
      dialog.setAttribute('tabindex', '-1');
      try { dialog.focus({ preventScroll: true }); } catch (e) { dialog.focus(); }
      return;
    }

    /* Focus the input: the customer came here to type. One animation frame was not reliably
       enough — the element is laid out but the focus call still landed on <body> — so this
       retries briefly rather than assuming. preventScroll stops the page lurching behind the
       overlay as focus moves. */
    var tries = 0;
    (function focusInput() {
      var input = dialog.querySelector('#llm-cah-q') || closeBtn;
      try { input.focus({ preventScroll: true }); } catch (e) { input.focus(); }
      if (document.activeElement !== input && tries++ < 5) setTimeout(focusInput, 60);
    })();
  }

  function close() {
    overlay.hidden = true;
    document.body.style.overflow = '';
    overlay.style.removeProperty('--llm-finder-top');
    overlay.style.removeProperty('--llm-finder-h');
    openBtn.setAttribute('aria-expanded', 'false');
    /* Return focus to where it came from, or the customer is dumped at the top of the page
       with no idea where they are. */
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  openBtn.addEventListener('click', open);
  closeBtn.addEventListener('click', close);

  // Click the backdrop (but not the dialog) to dismiss.
  overlay.addEventListener('mousedown', function (e) {
    if (e.target === overlay) close();
  });

  document.addEventListener('keydown', function (e) {
    if (overlay.hidden) return;
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;

    // Trap Tab inside the dialog.
    var items = focusable();
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });
})();
